import { Text } from "~/components/Text"
import { useEffect, useMemo, useState } from "react"
import {
  DatabaseOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Tag,
  Typography,
} from "antd"
import { Alert } from "~/components/Alert"
import type { TableProps } from "antd"
import {
  MOBILE_PHONE_PATTERN,
  BulkWalletEntryMode,
  OrderType,
  PaymentMethod,
  ProductCode,
} from "~/common/constants"
import { listStaffOrderOpLogs } from "~/api/staff"
import { formatAmount, formatDateTimeMinute } from "~/common/utils"
import {
  orderTypeLabel,
  bulkEntryModeLabel,
  paymentMethodLabel,
  productCodeLabel,
} from "~/pages/Staff/labels"
import { useStaffUserLookup } from "~/pages/Staff/useStaffUserLookup"
import { StaffUserCard } from "~/pages/Staff/StaffUserCard"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import { BulkWalletFields } from "./BulkWalletFields"
import { OrderLogDetailsModal } from "./OrderLogDetailsModal"
import { OrderConfirmationModal } from "./OrderConfirmationModal"
import {
  buildStaffOrderPayload,
  getStaffOrderProductCode,
  getStaffProductHelpText,
  getStaffBulkWalletAmount,
} from "./orderForm"
import type { StaffOrderFormValues } from "./orderForm"
import styles from "./index.css"

const { Title } = Typography

const walletRechargeAmountOptions = [100, 300, 1000]

const orderTypeColor: Record<OrderType, string> = {
  [OrderType.levelPackage]: "purple",
  [OrderType.walletPackage]: "blue",
  [OrderType.bulkWalletPackage]: "cyan",
}

export const Staff = () => {
  const [form] = Form.useForm<StaffOrderFormValues>()
  const {
    queriedUser,
    queryingUser,
    lookupUser,
    resetQueriedUser,
    setQueriedUser,
  } = useStaffUserLookup()
  const [pendingOrder, setPendingOrder] = useState<{
    payload: API.Staff.CreateStaffOrderOpLogParams
    mobile: string
  } | null>(null)
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)
  const orderType =
    Form.useWatch("orderType", form) || OrderType.bulkWalletPackage
  const isBulk = orderType === OrderType.bulkWalletPackage
  const selectedProductCode = useMemo(() => {
    return getStaffOrderProductCode(orderType, queriedUser)
  }, [orderType, queriedUser])
  const selectedProductLabel = productCodeLabel[selectedProductCode]

  const orderLogs = usePagedQuery({
    fetchPage: listStaffOrderOpLogs,
    initialFilters: {},
  })

  const queryUser = async () => {
    const { mobile } = await form.validateFields(["mobile"])
    await lookupUser(mobile)
  }

  const confirmSubmit = (values: StaffOrderFormValues) => {
    if (values.orderType !== OrderType.bulkWalletPackage || !queriedUser?.id)
      return
    const payload = buildStaffOrderPayload(values, queriedUser)
    setPendingOrder({ payload, mobile: queriedUser.mobile })
  }

  const columns: TableProps<API.Staff.StaffOrderOpLog>["columns"] = [
    {
      title: "录入时间",
      dataIndex: "created_t",
      width: 170,
      render: (value: string) => formatDateTimeMinute(value),
    },
    {
      title: "被充值手机号",
      dataIndex: "user_mobile",
      width: 140,
    },
    {
      title: "支付方式",
      dataIndex: "payment_method",
      width: 110,
      render: (value: PaymentMethod) => paymentMethodLabel[value] || "-",
    },
    {
      title: "订单类型",
      dataIndex: "order_type",
      width: 130,
      render: (value: OrderType) => {
        const label = orderTypeLabel[value]
        return label ? <Tag color={orderTypeColor[value]}>{label}</Tag> : "-"
      },
    },
    {
      title: "商品编码",
      dataIndex: "product_code",
      width: 150,
      render: (value: ProductCode) => productCodeLabel[value] || "-",
    },
    {
      title: "积点数量",
      dataIndex: "wallet_recharge_amount",
      width: 100,
      render: (value?: number) => value || "-",
    },
    {
      title: "购买单价（分/积点）",
      width: 150,
      render: (_, record) => record.purchase_unit_price ?? "-",
    },
    {
      title: "退款结算单价（分/积点）",
      width: 175,
      render: (_, record) => record.refund_unit_price ?? "-",
    },
    {
      title: "录入方式",
      width: 130,
      render: (_, record) =>
        record.order_type === OrderType.bulkWalletPackage
          ? bulkEntryModeLabel[
              record.bulk_entry_mode ?? BulkWalletEntryMode.quantity
            ]
          : "-",
    },
    {
      title: "大客户订单金额",
      width: 140,
      render: (_, record) =>
        record.order_type === OrderType.bulkWalletPackage
          ? formatAmount(getStaffBulkWalletAmount(record))
          : "-",
    },
    {
      title: "外部交易号",
      dataIndex: "external_payment_no",
      ellipsis: true,
    },
    {
      title: "订单ID",
      dataIndex: "order_id",
      ellipsis: true,
      render: (value?: string | null) => value || "-",
    },
    {
      title: "操作",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          disabled={!record.id}
          onClick={() => setSelectedLogId(record.id ?? null)}
        >
          详情
        </Button>
      ),
    },
  ]

  useEffect(() => {
    form.resetFields([
      "walletRechargeAmount",
      "purchaseUnitPrice",
      "refundUnitPrice",
      "bulkEntryMode",
      "rechargeAmountYuan",
    ])
  }, [form, orderType])

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>人工录入支付订单</Title>
        <Text type="secondary">
          由 Staff
          人工录入已支付的订单信息，系统将自动创建订单、支付交易并发放权益或积点。
        </Text>
      </Flex>

      <Flex
        className={styles.workspace}
        align="stretch"
        gap={18}
      >
        <section className={styles.formPanel}>
          <Form<StaffOrderFormValues>
            form={form}
            onFinish={confirmSubmit}
            colon={false}
            initialValues={{
              orderType: OrderType.bulkWalletPackage,
              paymentMethod: PaymentMethod.wechat,
            }}
            labelCol={{ flex: "180px" }}
            labelAlign="left"
            requiredMark
          >
            <Form.Item
              label="被充值手机号"
              required
            >
              <Flex gap={12}>
                <Form.Item
                  className={styles.inlineField}
                  name="mobile"
                  noStyle
                  rules={[
                    { required: true, message: "请输入被充值用户手机号" },
                    {
                      pattern: MOBILE_PHONE_PATTERN,
                      message: "手机号格式错误",
                    },
                  ]}
                >
                  <Input
                    placeholder="请输入被充值用户手机号"
                    onChange={resetQueriedUser}
                  />
                </Form.Item>
                <Button
                  icon={<SearchOutlined />}
                  loading={queryingUser}
                  onClick={queryUser}
                >
                  查询用户
                </Button>
              </Flex>
            </Form.Item>

            {queriedUser ? (
              <StaffUserCard
                className={styles.userCard}
                expiryLabel="有效期至"
                user={queriedUser}
              />
            ) : null}

            <Form.Item
              label="支付方式"
              name="paymentMethod"
              rules={[{ required: true, message: "请选择支付方式" }]}
            >
              <Select
                options={[
                  {
                    label: paymentMethodLabel[PaymentMethod.wechat],
                    value: PaymentMethod.wechat,
                  },
                  {
                    label: paymentMethodLabel[PaymentMethod.alipay],
                    value: PaymentMethod.alipay,
                  },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="外部支付渠道交易号"
              name="externalPaymentNo"
              extra={
                isBulk
                  ? "同一转账分给多个账号时，可手工填写 T123_0、T123_1 等不同标识；重试使用原标识。"
                  : undefined
              }
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "请输入外部支付渠道交易号",
                },
              ]}
            >
              <Input placeholder="请输入外部支付渠道交易号" />
            </Form.Item>

            <Form.Item
              label="订单类型"
              name="orderType"
              rules={[{ required: true, message: "请选择订单类型" }]}
            >
              <Select
                options={[
                  {
                    label: orderTypeLabel[OrderType.levelPackage],
                    value: OrderType.levelPackage,
                  },
                  {
                    label: orderTypeLabel[OrderType.walletPackage],
                    value: OrderType.walletPackage,
                  },
                  {
                    label: orderTypeLabel[OrderType.bulkWalletPackage],
                    value: OrderType.bulkWalletPackage,
                  },
                ]}
              />
            </Form.Item>

            <Form.Item label="商品编码">
              <Input
                value={selectedProductLabel}
                readOnly
              />
              <Text
                size="s"
                className={styles.helpText}
                type="secondary"
              >
                {getStaffProductHelpText(orderType, queriedUser)}
              </Text>
            </Form.Item>

            {isBulk ? (
              <BulkWalletFields className={styles.disabledAmount} />
            ) : orderType === OrderType.walletPackage ? (
              <Form.Item
                label="积点数量"
                name="walletRechargeAmount"
                rules={[{ required: true, message: "请选择积点数量" }]}
              >
                <Select
                  placeholder="请选择积点数量"
                  options={walletRechargeAmountOptions.map((value) => ({
                    label: `${value} 积点`,
                    value,
                  }))}
                />
              </Form.Item>
            ) : (
              <Form.Item label="积点数量">
                <InputNumber
                  className={styles.disabledAmount}
                  disabled
                  placeholder="会员等级套餐不需要填写"
                />
              </Form.Item>
            )}

            <Alert
              className={styles.summary}
              level="weak"
              showIcon
              title={getStaffProductHelpText(orderType, queriedUser)}
            />

            <Flex
              justify="flex-end"
              gap={12}
            >
              <Button
                onClick={() => {
                  form.resetFields()
                  setQueriedUser(null)
                }}
              >
                取消
              </Button>
              <Button
                disabled={
                  !isBulk ||
                  !queriedUser?.id ||
                  queryingUser ||
                  Boolean(pendingOrder)
                }
                type="primary"
                htmlType="submit"
              >
                确认录入
              </Button>
            </Flex>
          </Form>
        </section>

        <aside className={styles.rulePanel}>
          <Flex
            align="center"
            gap={8}
          >
            <InfoCircleOutlined className={styles.ruleIcon} />
            <Title level={4}>录入规则</Title>
          </Flex>
          <ul className={styles.ruleList}>
            {isBulk ? (
              <>
                <li>请确认财务已收到款项，并查询确认本次充值账号。</li>
                <li>
                  支持按积点数量或充值金额录入，购买单价为 22～30 分/积点。
                </li>
                <li>按金额录入时，到账积点按金额 ÷ 购买单价向上取整。</li>
                <li>到账数量为 1～100000 的整数，充值金额最多两位小数。</li>
                <li>退款结算单价为 20～30 分/积点，且不得低于购买单价。</li>
                <li>免费版、专业版均可录入，不改变会员等级，积点长期有效。</li>
                <li>
                  一次录入对应一个账号。同一转账可手工添加不同交易号后缀分别录入。
                </li>
                <li>
                  同一支付方式和完整交易号不会重复充值；失败重试使用原交易号。
                </li>
              </>
            ) : (
              <>
                <li>会员等级套餐仅支持专业版月度套餐。</li>
                <li>积点钱包套餐按用户等级展示免费版/专业版积点包。</li>
                <li>积点数量固定为 100、300、1000。</li>
                <li>同一支付方式与外部交易号不会重复充值。</li>
                <li>
                  如果是 Staff
                  自行录入的伪造订单，支付方式请选择「微信」；外部交易号请使用“年月日时分秒随机数”的纯数字格式，例如「20260721171112345678」，不要包含中文或英文字母。
                </li>
              </>
            )}
          </ul>
        </aside>
      </Flex>

      <section className={styles.logPanel}>
        <Flex
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <DatabaseOutlined className={styles.sectionIcon} />
            <Title level={4}>Staff 录入记录</Title>
          </Flex>
          <Button onClick={orderLogs.refresh}>刷新</Button>
        </Flex>
        <Table
          columns={columns}
          {...orderLogs.tableProps}
          rowKey={(record) => record.id || record.external_payment_no}
          scroll={{ x: 1800 }}
        />
      </section>
      {pendingOrder ? (
        <OrderConfirmationModal
          payload={pendingOrder.payload}
          mobile={pendingOrder.mobile}
          onCancel={() => setPendingOrder(null)}
          onSuccess={() => {
            setPendingOrder(null)
            form.resetFields()
            setQueriedUser(null)
            orderLogs.search(orderLogs.filters)
          }}
        />
      ) : null}
      {selectedLogId ? (
        <OrderLogDetailsModal
          opLogId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      ) : null}
    </Flex>
  )
}
